import React, { useEffect, useRef, useState } from 'react';
import {
  applyCalculatorInput,
  createCalculatorState,
  UCAT_CALCULATOR_LABELS,
} from '../utils/ucatPearsonCalculator';

// Pixel geometry measured from
// docs/research/ucat-pearson-vue-harvest/sample/uk-pt-a/14-calculator-pearson-detail.png.
const BODY_WIDTH = 276;
const BODY_HEIGHT = 532;
const TITLE_BAR_HEIGHT = 53;
const DISPLAY_TOP_GAP = 31;
const DISPLAY_TOP = TITLE_BAR_HEIGHT + DISPLAY_TOP_GAP;
const DISPLAY_INSET_LEFT = 36;
const DISPLAY_INSET_RIGHT = 33;
const DISPLAY_WIDTH = BODY_WIDTH - DISPLAY_INSET_LEFT - DISPLAY_INSET_RIGHT;
const DISPLAY_HEIGHT = 46;
const BRAND_STRIP_GAP = 10;
const BRAND_STRIP_HEIGHT = 21;
const KEYPAD_TOP = 237;
const KEY_HEIGHT = 30;
const KEY_ROW_GAP = 15;
const KEYPAD_LEFT_MARGIN = 23;
const COL_WIDTHS = [45, 45, 46, 45];
const COL_GAPS = [10, 11, 32];
const EQUALS_HEIGHT = KEY_HEIGHT * 2 + KEY_ROW_GAP;

// Colours sampled from the harvest PNG.
const COLOR_TITLE_BAR = '#05619F';
const COLOR_SHELL_BODY = '#476CB2';
const COLOR_KEYPAD_PANEL = '#4569AD';
const COLOR_KEY_RED = '#DA1F27';
const COLOR_KEY_WHITE = '#FFFFFF';
const COLOR_DISPLAY_BG = '#FFFFFF';
const COLOR_DISPLAY_TEXT = '#000000';

const RED_KEYS = new Set(['+/-', 'sqrt', '%', '/', 'MRC', 'M-', 'M+', '*', '-', '+', '=', 'ON/C']);

// [row][col] — col 4 is only populated on rows 1-4; "=" spans rows 5-6 there.
const KEY_ROWS = [
  ['+/-', 'sqrt', '%', '/'],
  ['MRC', 'M-', 'M+', '*'],
  ['7', '8', '9', '-'],
  ['4', '5', '6', '+'],
  ['1', '2', '3', '='],
  ['ON/C', '0', '.'],
];

const displayLabel = (key) => {
  if (key === 'MRC') return UCAT_CALCULATOR_LABELS.memoryRecall;
  if (key === 'M-') return UCAT_CALCULATOR_LABELS.memoryMinus;
  if (key === 'M+') return UCAT_CALCULATOR_LABELS.memoryPlus;
  if (key === 'ON/C') return UCAT_CALCULATOR_LABELS.onClear;
  if (key === '/') return UCAT_CALCULATOR_LABELS.divide;
  if (key === '*') return UCAT_CALCULATOR_LABELS.multiply;
  if (key === '-') return UCAT_CALCULATOR_LABELS.minus;
  if (key === '+') return UCAT_CALCULATOR_LABELS.plus;
  if (key === '=') return UCAT_CALCULATOR_LABELS.equals;
  if (key === '%') return UCAT_CALCULATOR_LABELS.percent;
  if (key === 'sqrt') return UCAT_CALCULATOR_LABELS.sqrt;
  if (key === '.') return UCAT_CALCULATOR_LABELS.decimal;
  if (key === '+/-') return UCAT_CALCULATOR_LABELS.negate;
  return key;
};

function colLeft(colIndex) {
  let left = KEYPAD_LEFT_MARGIN;
  for (let i = 0; i < colIndex; i += 1) {
    left += COL_WIDTHS[i] + COL_GAPS[i];
  }
  return left;
}

function rowTop(rowIndex) {
  return KEYPAD_TOP + rowIndex * (KEY_HEIGHT + KEY_ROW_GAP);
}

export default function UcatPearsonCalculator({
  open,
  enabled = true,
  onClose,
  onEnable,
  onDisable,
}) {
  const [state, setState] = useState(createCalculatorState);
  const [position, setPosition] = useState({ top: 48, left: 24 });
  const rootRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        if (typeof onDisable === 'function') onDisable();
        else onClose?.();
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose, onDisable]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = (event) => {
      if (!dragRef.current) return;
      const { offsetX, offsetY, width, height } = dragRef.current;
      const parent = rootRef.current?.offsetParent;
      const bounds = parent
        ? { width: parent.clientWidth, height: parent.clientHeight }
        : { width: window.innerWidth, height: window.innerHeight };
      const maxLeft = Math.max(0, bounds.width - width);
      const maxTop = Math.max(0, bounds.height - height);
      const nextLeft = Math.min(Math.max(0, event.clientX - offsetX), maxLeft);
      const nextTop = Math.min(Math.max(0, event.clientY - offsetY), maxTop);
      setPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [open]);

  const startDrag = (event) => {
    if (!enabled) {
      onEnable?.();
      return;
    }
    // Below 768px the calculator is a fixed-position bottom sheet with no
    // repositioning needed — dragging is disabled outright, not just
    // visually hidden, so no dead-but-functional handle is left behind.
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) return;
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    dragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    event.preventDefault();
  };

  useEffect(() => {
    if (!open || !enabled) return undefined;
    const onKey = (event) => {
      if (!rootRef.current || !rootRef.current.contains(event.target)) return;
      const key = event.key;
      if (key >= '0' && key <= '9') {
        event.preventDefault();
        setState((prev) => applyCalculatorInput(prev, key));
        return;
      }
      if (key === '.') {
        event.preventDefault();
        setState((prev) => applyCalculatorInput(prev, '.'));
        return;
      }
      if (key === '+' || key === '-' || key === '*' || key === '/') {
        event.preventDefault();
        setState((prev) => applyCalculatorInput(prev, key));
        return;
      }
      if (key === 'Enter' || key === '=') {
        event.preventDefault();
        setState((prev) => applyCalculatorInput(prev, '='));
        return;
      }
      if (key === '%') {
        event.preventDefault();
        setState((prev) => applyCalculatorInput(prev, '%'));
        return;
      }
      if (key === 'Escape') {
        event.preventDefault();
        setState((prev) => applyCalculatorInput(prev, 'ON/C'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, enabled]);

  if (!open) return null;

  const press = (key) => {
    if (!enabled) {
      onEnable?.();
      return;
    }
    setState((prev) => applyCalculatorInput(prev, key));
  };

  const showMemoryIndicator = Boolean(state.memory);

  const gapAboveKeypad = KEYPAD_TOP - (DISPLAY_TOP + DISPLAY_HEIGHT + BRAND_STRIP_GAP + BRAND_STRIP_HEIGHT);
  const bottomMargin = BODY_HEIGHT - (rowTop(KEY_ROWS.length - 1) + KEY_HEIGHT);

  return (
    <div
      ref={rootRef}
      data-ucat-calculator="true"
      tabIndex={0}
      className="ucat-pearson-calculator"
      onMouseDown={() => { if (!enabled) onEnable?.(); }}
      aria-label="Calculator"
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        zIndex: 40,
        width: BODY_WIDTH,
        background: COLOR_SHELL_BODY,
        boxShadow: '0 8px 18px rgba(0,0,0,0.28)',
        fontFamily: 'Tahoma, Arial, Helvetica, sans-serif',
        opacity: enabled ? 1 : 0.55,
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .ucat-pearson-calculator {
            position: fixed !important;
            top: auto !important;
            left: 50% !important;
            right: auto !important;
            bottom: 0 !important;
            width: ${BODY_WIDTH}px !important;
            transform: translateX(-50%) scale(min(1, 100vw / ${BODY_WIDTH}px, 100dvh / ${BODY_HEIGHT}px)) !important;
            transform-origin: bottom center !important;
          }
          .ucat-pearson-calculator-dragbar {
            cursor: default !important;
          }
        }
      `}</style>
      <div
        className="ucat-pearson-calculator-dragbar"
        onMouseDown={startDrag}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: TITLE_BAR_HEIGHT,
          padding: '0 14px',
          background: COLOR_TITLE_BAR,
          color: '#FFFFFF',
          fontSize: 13,
          fontWeight: 700,
          cursor: enabled ? 'move' : 'default',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
          >
            <rect x="3" y="1.5" width="10" height="13" rx="1.5" fill="none" stroke="#FFFFFF" strokeWidth="1.25" />
            <rect x="5" y="3.5" width="6" height="2.5" fill="#FFFFFF" />
            <rect x="5" y="7.5" width="1.6" height="1.6" fill="#FFFFFF" />
            <rect x="7.2" y="7.5" width="1.6" height="1.6" fill="#FFFFFF" />
            <rect x="9.4" y="7.5" width="1.6" height="1.6" fill="#FFFFFF" />
            <rect x="5" y="10" width="1.6" height="1.6" fill="#FFFFFF" />
            <rect x="7.2" y="10" width="1.6" height="1.6" fill="#FFFFFF" />
            <rect x="9.4" y="10" width="1.6" height="1.6" fill="#FFFFFF" />
          </svg>
          <span style={{ fontFamily: '"Trebuchet MS", sans-serif', fontWeight: 700 }}>Calculator</span>
        </span>
        <button
          type="button"
          aria-label="Close calculator"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={onClose}
          style={{
            background: 'transparent',
            border: '1px solid #FFFFFF',
            color: '#FFFFFF',
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            width: 18,
            height: 18,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
          }}
        >
          ×
        </button>
      </div>
      <div
        className="ucat-pearson-calculator-display-row"
        style={{
          margin: `${DISPLAY_TOP_GAP}px ${DISPLAY_INSET_RIGHT}px 0 ${DISPLAY_INSET_LEFT}px`,
          width: DISPLAY_WIDTH,
          height: DISPLAY_HEIGHT,
          background: COLOR_DISPLAY_BG,
          color: COLOR_DISPLAY_TEXT,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '0 8px',
          fontSize: 24,
          fontVariantNumeric: 'tabular-nums',
          boxSizing: 'border-box',
        }}
      >
        {showMemoryIndicator ? (
          <span style={{ position: 'absolute', right: 8, fontSize: 13, fontWeight: 700 }}>M</span>
        ) : null}
        {state.display}
      </div>
      <div
        className="ucat-pearson-calculator-brand-strip"
        data-testid="ucat-calculator-brand"
        style={{
          marginTop: BRAND_STRIP_GAP,
          height: BRAND_STRIP_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          color: '#FFFFFF',
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <g fill="#FFFFFF" fillRule="evenodd">
            <polygon points="3.2,5.2 7,5.2 7,1.6 11.2,1.6 11.2,5.2 14.4,5.2 14.4,9.4 12.6,14 8.2,14.6 5.4,12.2 1.6,10.4 1.6,7.6" />
            <polygon points="8.80,6.65 9.16,7.70 10.27,7.72 9.39,8.39 9.71,9.45 8.80,8.82 7.89,9.45 8.21,8.39 7.33,7.72 8.44,7.70" />
          </g>
        </svg>
        <span
          style={{
            fontFamily: 'Georgia, "Times New Roman", Times, serif',
            fontVariant: 'small-caps',
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: 9,
            letterSpacing: '0.06em',
            color: '#FFFFFF',
          }}
        >
          TEXAS INSTRUMENTS
        </span>
        <span
          style={{
            fontFamily: 'Rockwell, "Roboto Slab", serif',
            fontWeight: 800,
            fontSize: 17,
            fontStyle: 'normal',
            letterSpacing: '0.02em',
            color: '#FFFFFF',
          }}
        >
          TI-108
        </span>
      </div>
      <div
        className="ucat-pearson-calculator-keypad"
        style={{
          position: 'relative',
          marginTop: gapAboveKeypad,
          width: BODY_WIDTH,
          height: rowTop(KEY_ROWS.length - 1) - KEYPAD_TOP + KEY_HEIGHT + bottomMargin,
          background: COLOR_KEYPAD_PANEL,
        }}
      >
        {KEY_ROWS.map((row, rowIndex) => row.map((key, colIndex) => {
          const isRed = RED_KEYS.has(key);
          const isEquals = key === '=';
          return (
            <button
              key={key}
              type="button"
              className={isEquals ? 'ucat-pearson-calculator-key-equals' : undefined}
              onClick={() => press(key)}
              aria-label={displayLabel(key)}
              style={{
                position: 'absolute',
                top: rowTop(rowIndex) - KEYPAD_TOP,
                left: colLeft(colIndex),
                width: COL_WIDTHS[colIndex],
                height: isEquals ? EQUALS_HEIGHT : KEY_HEIGHT,
                border: 'none',
                borderRadius: 3,
                background: isRed ? COLOR_KEY_RED : COLOR_KEY_WHITE,
                color: isRed ? '#FFFFFF' : '#000000',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {displayLabel(key)}
            </button>
          );
        }))}
      </div>
    </div>
  );
}
